import { useCallback, useState } from 'react';

import { useToast } from '@/components/ui/toast';
import { REVOKE_MS, saveUrl } from '@/lib/download';

import { copyErrorCopy, copyFileName } from './presentation';
import type { Purpose } from './types';
import { usePrintCopy, type CopyRequest } from './use-watermark';

export interface CopyRun {
  entryId: string;
  /** Código del documento: nombre de respaldo si el servidor no manda el suyo. */
  code: string;
  purpose: Purpose;
  mode: CopyRequest['mode'];
}

export interface WatermarkedCopy {
  run: (req: CopyRun, onDone: () => void) => void;
  pending: boolean;
  error: string | null;
  reset: () => void;
}

const BLOCKED =
  'El navegador bloqueó la pestaña nueva. Permite las ventanas emergentes de este sitio e intenta de nuevo.';

/** Copia controlada desde la ficha (so=400). La vista de impresión abre la pestaña en el
 *  mismo clic (si esperara la respuesta, el bloqueador de ventanas la frenaría) y le
 *  asigna el PDF al llegar; la descarga lo guarda con el nombre del servidor. */
export function useWatermarkedCopy(): WatermarkedCopy {
  const copy = usePrintCopy();
  const { showToast } = useToast();
  const [error, setError] = useState<string | null>(null);

  const run = useCallback(
    (req: CopyRun, onDone: () => void): void => {
      setError(null);
      const tab = req.mode === 'print_view' ? window.open('', '_blank') : null;
      if (req.mode === 'print_view' && tab === null) {
        setError(BLOCKED);
        return;
      }
      if (tab !== null) {
        // La pestaña solo muestra el PDF: sin acceso de vuelta a la app.
        tab.opener = null;
        tab.document.title = 'Preparando la copia…';
      }
      // mutateAsync y no los callbacks de mutate: estos no corren si el diálogo se desmonta
      // con la copia en curso (Atrás del navegador), y la pestaña quedaría huérfana con
      // el evento ya registrado en el servidor.
      copy.mutateAsync({ entryId: req.entryId, purpose: req.purpose, mode: req.mode }).then(
        ({ blob, folio, filename }) => {
          const url = URL.createObjectURL(blob);
          if (tab !== null) {
            tab.location.href = url;
          } else {
            saveUrl(url, filename ?? copyFileName(req.code, req.purpose));
          }
          window.setTimeout(() => URL.revokeObjectURL(url), REVOKE_MS);
          showToast(folio ? `Copia registrada con el folio ${folio}` : 'Copia registrada');
          onDone();
        },
        (err: unknown) => {
          tab?.close();
          setError(copyErrorCopy(err));
        },
      );
    },
    [copy, showToast],
  );

  return {
    run,
    pending: copy.isPending,
    error,
    reset: useCallback(() => setError(null), []),
  };
}
